import { Lock } from 'lucide-react';
import { centsToDollars } from '../../utils/useStatementModel';
import { InfoTip } from './InfoTip';

interface RuleChipProps {
  label: string;
  locked?: boolean;
  active?: boolean;
  /** Live dollar impact of the rule (cents removed from the base), shown under the label. */
  impactCents?: number;
  /** Plain-language explanation surfaced via an ⓘ tooltip (read-only UX aid). */
  info?: string;
  onToggle?: () => void;
}

/**
 * One statement rule. Core rules are locked (policy, not preference); what-if
 * rules toggle on click and show their live dollar impact. An optional ⓘ tooltip
 * explains what the rule means. The what-if variant keeps its ⓘ trigger as a
 * sibling of the toggle button (never nested) so the markup stays valid.
 */
export function RuleChip({ label, locked = false, active = true, impactCents, info, onToggle }: RuleChipProps) {
  if (locked) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
        <Lock className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
        <span className="flex-1">{label}</span>
        {info && <InfoTip text={info} />}
      </div>
    );
  }
  return (
    <div
      className={`relative w-full rounded-lg border transition-colors ${
        active ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={active}
        className={`w-full rounded-lg px-3 py-2 pr-8 text-left text-sm ${active ? 'text-gray-900' : 'text-gray-400'}`}
      >
        <span className="flex items-center gap-2">
          <span
            aria-hidden
            className={`inline-flex h-4 w-4 items-center justify-center rounded border text-[10px] font-bold ${
              active ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-gray-300 bg-white'
            }`}
          >
            {active ? '✓' : ''}
          </span>
          <span className="font-medium">{label}</span>
        </span>
        {impactCents !== undefined && (
          <span className={`mt-0.5 block pl-6 text-xs tabular-nums ${active ? 'text-gray-500' : 'text-gray-400'}`}>
            −${centsToDollars(impactCents)}
            {!active && ' (added back)'}
          </span>
        )}
      </button>
      {info && (
        <span className="absolute right-1.5 top-1.5">
          <InfoTip text={info} />
        </span>
      )}
    </div>
  );
}
